import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl/dist';

import styled from 'styled-components';
import { AccordionPanel } from 'app-components';

import { useAppDispatch, useAppSelector, useModal, useStateCode } from '../../../../hooks';
import EnrolledMembersWrapper from '../EnrolledMembers/EnrolledMembersWrapper';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { PlAN_TYPES } from '../../../../constants/individualPortal/customGrouping/customGrouping.constants';
import UnenrolledMembersWrapper from '../UnenrolledMembers/UnenrolledMembersWrapper';
import GroupedMembersWrapper from '../GroupedMembers/GroupedMembersWrapper';
import { environmentSelector } from '../../../../selectors/commonSelectors';
import { APP_TYPES } from '../../../../constants/individualPortal/dashboard/dashboardModelTypes';
import useCustomGrouping from '../../hooks/useCustomGrouping';

const StyledAccordionContainer = styled.div`
    & > * {
        border: 0px;
    }
    .header-container {
        background-color: ${({ theme }) => theme.gray10};
        border-radius: 0px;
        padding: 0px 1%;
        letter-spacing: 1px;
    }

    .content {
        border-top: 0px;
        background-color: white;
        padding-bottom: 12px !important;
    }

    .toggle-icon {
        text-decoration: none;
        color: ${({ theme }) => theme.primaryDark};
    }
`;

const StyledAccordionContainerWithBorder = styled.div`
    & > * {
        border: ${({ theme }) => `1px solid ${theme.gray8}`};
    }

    .header-container {
        background-color: ${({ theme }) => theme.gray10};
        border-radius: 0px;
        padding: 0px 1%;
        letter-spacing: 1px;
    }

    .content {
        border-top: 0px;
    }
`;

const DentalPlan = props => {
    const { setActiveTab, daysRemaining } = props;

    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const dispatch = useAppDispatch();
    const {
        isHouseHoldAPTCEligible,
        areAllMembersEnrolledInGroup,
        isAllEnrolledInHealth,
        updateDentalActivePassiveRenewalGroups,
        areAllMembersAddedToGroups,
    } = useCustomGrouping({ dispatch });

    const {
        isAnyDentalGroupEnrolled = false,
        isDentalRenewalGroupAvailable = false,
        DentalGroupEnrolledMembersCount = 0,
        dentalGroups = [],
        unEnrolledDentalGroup = [],
        hasAIMembers = false,
        addDentalMembersGroup = [],
        applicationType,
        groupList = [],
        isInsideOEEnrollmentWindow,
        isRenewalApplication,
        isHealthRenewalGroupAvailable,
        isIneligibleMembersPresent,
        isDentalRenewed,
    } = customGroupingState;

    useEffect(() => {
        // Show modal to encourage consumers to enroll in health plans prior to dental.
        if (environment.flags.allowDentalWithoutHealthEnrollment) {
            if (
                [APP_TYPES.QEP, APP_TYPES.OE].includes(applicationType) &&
                isHouseHoldAPTCEligible(groupList) &&
                !areAllMembersEnrolledInGroup(groupList)
            ) {
                openModal('healthBeforeDentalModal', { setActiveTab });
            }
        } else {
            if (dentalGroups.length && isAllEnrolledInHealth(dentalGroups?.[0])) setActiveTab(1);
            if (dentalGroups.length && isInsideOEEnrollmentWindow && applicationType === APP_TYPES.OE) {
                updateDentalActivePassiveRenewalGroups({
                    isRenewalApplication,
                    isHealthRenewalGroupAvailable,
                    isIneligibleMembersPresent,
                    isDentalRenewalGroupAvailable,
                    isDentalRenewed,
                    groupList,
                    dentalGroups,
                });
            }
        }
    }, []);

    const { openModal } = useModal();
    const { isApplicationNotToConsiderForDentalVisible, isAIANMemberAlertVisible, isAddMemberWarningVisible, isBoldHeader } =
        useStateCode();
    const environment = useAppSelector(environmentSelector);

    const AccordionHeader = () => {
        return (
            <p className={`${isBoldHeader ? 'bold-header' : ''}`} data-testid="accordion-header">
                <FormattedMessage
                    id={
                        isDentalRenewalGroupAvailable
                            ? 'indportal.customGrouping.coverageYearMemberEnrolled'
                            : 'indportal.customGrouping.enrolledMember'
                    }
                    values={{ memberCount: DentalGroupEnrolledMembersCount }}
                />
            </p>
        );
    };

    function areAllMembersAddedToDentalGroup() {
        return unEnrolledDentalGroup.every(group => {
            return areAllMembersAddedToGroups(group);
        });
    }

    return (
        <>
            <ConditionalRendering isTrue={isAnyDentalGroupEnrolled}>
                <ConditionalRendering isTrue={isDentalRenewalGroupAvailable}>
                    <ConditionalRendering isTrue={!isApplicationNotToConsiderForDentalVisible}>
                        <FormattedMessage
                            id="indportal.customGrouping.dentalChangeGroup1"
                            data-testid="dental-change-group1"
                        />{' '}
                        <FormattedMessage
                            id="indportal.customGrouping.dentalChangeGroup2"
                            data-testid="dental-change-group2"
                        />
                        <br />
                        <FormattedMessage
                            id="indportal.customGrouping.dentalRenewalMessage"
                            data-testid="dental-renewal-message"
                        />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={isApplicationNotToConsiderForDentalVisible}>
                        <p data-testid="renew-change-group1">
                            <FormattedMessage id="indportal.customGrouping.renewChangeGroup1" />{' '}
                            <a
                                href="javascript:void(0);"
                                onClick={() =>
                                    openModal('changeApplicationNotToConsider', { typeOfPlan: PlAN_TYPES.DENTAL })
                                }
                                data-testid="change-application-link"
                            >
                                <FormattedMessage
                                    id="indportal.customGrouping.renewChangeGroup2"
                                    data-testid="renew-change-group2"
                                />
                            </a>
                        </p>
                    </ConditionalRendering>
                </ConditionalRendering>
                <StyledAccordionContainer data-testid="styled-accordion-container">
                    <AccordionPanel
                        id="dentalAccordian"
                        isExpandable={true}
                        header={<AccordionHeader data-testid="accordion-header" />}
                        isInitialExpanded={
                            environment?.enableDentalCustomGrouping && addDentalMembersGroup.length === 0
                        }
                        data-testid="accordion-panel-enrolled"
                    >
                        <EnrolledMembersWrapper
                            {...{ enrolledGroups: dentalGroups }}
                            planType={PlAN_TYPES.DENTAL}
                            data-testid="enrolled-members-wrapper"
                        />
                    </AccordionPanel>
                </StyledAccordionContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={unEnrolledDentalGroup.length && !areAllMembersAddedToDentalGroup()}>
                <>
                    <p data-testid="whom-you-are-shopping-dental" className={`${isBoldHeader ? 'bold-header' : ''} mt-4`}>
                        <FormattedMessage
                            id="indportal.customGrouping.whomYouAreShoppingDental"
                            data-testid="whom-you-are-shopping-dental-message"
                        />
                    </p>
                    <ConditionalRendering isTrue={isAIANMemberAlertVisible}>
                        <p data-testid="aian-member-alert">
                            <FormattedMessage
                                id={
                                    hasAIMembers
                                        ? 'indportal.customGrouping.aiAnMember1'
                                        : 'indportal.customGrouping.aiAnMember2'
                                }
                                data-testid="aian-member-message"
                            />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isAIANMemberAlertVisible}>
                        <p data-testid="aian-member2">
                            <FormattedMessage id="indportal.customGrouping.aiAnMember2" />
                        </p>
                        <p data-testid="aian-member3">
                            <FormattedMessage id="indportal.customGrouping.aiAnMember3" />
                        </p>
                        <p data-testid="aian-member4">
                            <FormattedMessage id="indportal.customGrouping.aiAnMember4" />
                        </p>
                        <p data-testid="aian-member5">
                            <FormattedMessage id="indportal.customGrouping.aiAnMember5" />
                        </p>
                    </ConditionalRendering>

                    <p data-testid="shop-for-these" className={`${isBoldHeader ? 'bold-header' : ''}`}>
                        <FormattedMessage
                            id="indportal.customGrouping.shopForThese"
                            data-testid="shop-for-these-message"
                        />
                    </p>

                    <ConditionalRendering isTrue={!!dentalGroups.length}>
                        <UnenrolledMembersWrapper
                            unenrolledGroups={unEnrolledDentalGroup}
                            planType={PlAN_TYPES.DENTAL}
                            data-testid="unenrolled-members-wrapper"
                        />
                    </ConditionalRendering>
                </>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!!addDentalMembersGroup.length && environment?.enableDentalCustomGrouping}>
                <StyledAccordionContainerWithBorder
                    className="mt-4"
                    data-testid="styled-accordion-container-with-border"
                >
                    <AccordionPanel
                        id="dentalAccordianUnenrolled"
                        isExpandable={false}
                        header={
                            <p className={`${isBoldHeader ? 'bold-header' : ''}`}>
                                <FormattedMessage
                                    id="indportal.customGrouping.selectOption"
                                    data-testid="select-option"
                                />
                            </p>
                        }
                        isInitialExpanded={true}
                        data-testid="accordion-panel-unenrolled"
                    >
                        <ConditionalRendering isTrue={isAddMemberWarningVisible}>
                            <p data-testid="add-members-warning">
                                <FormattedMessage id="indportal.customGrouping.addMembers.warning1" />
                            </p>
                            <p>
                                <FormattedMessage
                                    id="indportal.customGrouping.addMembers.warning2"
                                    values={{ daysRemaining }}
                                    data-testid="add-members-warning2"
                                />
                            </p>
                        </ConditionalRendering>
                        <GroupedMembersWrapper
                            groups={addDentalMembersGroup}
                            planType={PlAN_TYPES.DENTAL}
                            data-testid="grouped-members-wrapper"
                        />
                    </AccordionPanel>
                </StyledAccordionContainerWithBorder>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!dentalGroups.length}>
                <p className="alert alert-info" data-testid="no-dental-group">
                    <FormattedMessage id="indportal.customGrouping.noDentalGroup" />
                </p>
            </ConditionalRendering>
        </>
    );
};

export default DentalPlan;
